import { useEffect, useState } from "react";
import { Sparkles, Eye, MousePointerClick, ShoppingCart, Bot, ShieldOff, TrendingUp } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";

const RAIL_LABEL = {
  "similar": "Similar Products", "frequently-bought": "Frequently Bought Together",
  "trending": "Trending (Home)", "trending-shop": "Trending (Shop)", "deals": "Today's Deals",
  "cart": "Cart complementary", "for-you": "Recommended for you",
};

export default function AdminRecommendations() {
  const [data, setData] = useState(null);
  const [series, setSeries] = useState([]);

  useEffect(() => {
    api.get("/marketplace/admin/recommendations/analytics").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/marketplace/admin/recommendations/ctr-timeseries?days=14").then((r) => setSeries(r.data.series || [])).catch(() => {});
  }, []);

  const cards = data ? [
    { k: "impressions", label: "Impressions", icon: Eye, v: data.impressions },
    { k: "clicks", label: "Clicks", icon: MousePointerClick, v: data.clicks },
    { k: "ctr", label: "Overall CTR", icon: Sparkles, v: `${data.overall_ctr}%` },
    { k: "add_to_cart", label: "Added to Cart", icon: ShoppingCart, v: data.add_to_cart },
    { k: "ai", label: "AI Recommendations", icon: Bot, v: data.ai_recommendations },
    { k: "optout", label: "Personalization opt-outs", icon: ShieldOff, v: data.personalization_opted_out },
  ] : [];

  return (
    <div data-testid="admin-recommendations">
      <PageHeader title="Recommendations" icon={Sparkles}
        subtitle="Performance of the centralized recommendation engine — real recorded events only, never fabricated data." />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {cards.map((c) => (
          <div key={c.k} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`reco-metric-${c.k}`}>
            <c.icon className="h-5 w-5 text-blue-500" />
            <p className="mt-2 text-2xl font-extrabold text-slate-900">{data ? (c.v ?? 0) : "—"}</p>
            <p className="text-xs text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4" data-testid="reco-ctr-chart">
        <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700"><TrendingUp className="h-4 w-4 text-blue-500" /> Recommendation CTR over time (last 14 days)</h2>
        <p className="mb-3 text-xs text-slate-500">Daily click-through rate across all recommendation rails — spot which days convert best.</p>
        {series.some((d) => d.impressions > 0) ? (
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 5, right: 10, bottom: 5, left: -18 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(d) => (d || "").slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit="%" />
                <Tooltip formatter={(v, n) => [n === "ctr" ? `${v}%` : v, n]} labelStyle={{ fontSize: 12 }} contentStyle={{ fontSize: 12, borderRadius: 12 }} />
                <Line type="monotone" dataKey="ctr" name="CTR" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="reco-ctr-empty">No recommendation activity in the last 14 days yet. The trend line appears as shoppers view and click rails.</p>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="reco-by-rail">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Performance by rail</h2>
        {(!data || (data.by_rail || []).length === 0) ? (
          <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="reco-empty">
            No recommendation activity recorded yet. Data appears as shoppers view and click recommendation rails.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs uppercase text-slate-400">
              <th className="py-2">Rail</th><th className="py-2">Impressions</th><th className="py-2">Clicks</th><th className="py-2">Add to cart</th><th className="py-2">CTR</th>
            </tr></thead>
            <tbody>
              {data.by_rail.map((r) => (
                <tr key={r.rail} className="border-b border-slate-50" data-testid={`reco-row-${r.rail}`}>
                  <td className="py-2.5 font-medium text-slate-700">{RAIL_LABEL[r.rail] || r.rail}</td>
                  <td className="py-2.5">{r.impressions}</td>
                  <td className="py-2.5">{r.clicks}</td>
                  <td className="py-2.5">{r.add_to_cart}</td>
                  <td className="py-2.5 font-semibold text-emerald-600">{r.ctr}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data?.note && <p className="mt-3 text-xs text-slate-400">{data.note}</p>}
    </div>
  );
}
